/**
 * Store Distance Calculator
 * Calculates distance between store location and user's current location
 */
(function() {
  'use strict';

  class StoreDistanceCalculator {
    constructor(options = {}) {
      this.storeLatitude = options.storeLatitude || 0;
      this.storeLongitude = options.storeLongitude || 0;
      this.distanceElement = options.distanceElement || null;
      this.unit = options.unit || 'km'; // 'km' or 'miles'
      this.loadingText = options.loadingText || 'Calculating distance...';
      
      if (!this.distanceElement) {
        console.error('Distance element is required');
        return;
      }

      this.init();
    }

    /**
     * Haversine formula to calculate distance between two coordinates
     * @param {number} lat1 - First latitude
     * @param {number} lon1 - First longitude
     * @param {number} lat2 - Second latitude
     * @param {number} lon2 - Second longitude
     * @returns {number} Distance in kilometers
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371; // Radius of the Earth in kilometers
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c; // Distance in kilometers
      return distance;
    }

    /**
     * Convert kilometers to miles
     * @param {number} km - Distance in kilometers
     * @returns {number} Distance in miles
     */
    kmToMiles(km) {
      return km * 0.621371;
    }

    /**
     * Format distance based on unit preference
     * @param {number} distanceKm - Distance in kilometers
     * @returns {string} Formatted distance string
     */
    formatDistance(distanceKm) {
      if (this.unit === 'miles') {
        const distanceMiles = this.kmToMiles(distanceKm);
        if (distanceMiles < 0.1) {
          // Convert to feet for very short distances
          const feet = Math.round(distanceMiles * 5280);
          return `${feet} feet away`;
        } else {
          return `${distanceMiles.toFixed(1)} miles away`;
        }
      } else {
        // Kilometers (default)
        if (distanceKm < 1) {
          const meters = Math.round(distanceKm * 1000);
          return `${meters} meters away`;
        } else {
          return `${distanceKm.toFixed(1)} kilometers away`;
        }
      }
    }

    /**
     * Handle successful geolocation
     * @param {Position} position - Geolocation position object
     */
    handleLocationSuccess(position) {
      const userLatitude = position.coords.latitude;
      const userLongitude = position.coords.longitude;
      
      const distance = this.calculateDistance(
        this.storeLatitude, 
        this.storeLongitude, 
        userLatitude, 
        userLongitude
      );
      
      this.distanceElement.textContent = this.formatDistance(distance);
    }

    /**
     * Handle geolocation error
     * @param {PositionError} error - Geolocation error object
     */
    handleLocationError(error) {
      console.error('Error getting location:', error);
      let errorMessage;
      
      switch(error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = "";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = "";
          break;
        case error.TIMEOUT:
          errorMessage = "Location request timeout";
          break;
        default:
          errorMessage = "Unable to get location";
          break;
      }
      
      this.distanceElement.textContent = errorMessage;
    }

    /**
     * Get user's current position and calculate distance
     */
    getUserLocation() {
      if (!navigator.geolocation) {
        this.distanceElement.textContent = "Geolocation not supported";
        return;
      }

      // Show loading text
      this.distanceElement.textContent = this.loadingText;

      navigator.geolocation.getCurrentPosition(
        (position) => this.handleLocationSuccess(position),
        (error) => this.handleLocationError(error),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    }

    /**
     * Change unit preference and recalculate if location is already known
     * @param {string} newUnit - 'km' or 'miles'
     */
    setUnit(newUnit) {
      if (newUnit !== 'km' && newUnit !== 'miles') {
        console.error('Unit must be "km" or "miles"');
        return;
      }
      
      this.unit = newUnit;
      
      // If we have a distance already calculated, update the display
      const currentText = this.distanceElement.textContent;
      if (currentText && !currentText.includes('Calculating') && !currentText.includes('denied') && !currentText.includes('unavailable')) {
        // Recalculate with new unit (this would require storing the last position)
        // For now, just trigger a new location request
        this.getUserLocation();
      }
    }

    /**
     * Initialize the distance calculator
     */
    init() {
      // Auto-start calculation when page loads
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.getUserLocation());
      } else {
        this.getUserLocation();
      }
    }
  }

  // Make it globally available
  window.StoreDistanceCalculator = StoreDistanceCalculator;

  // Enhanced auto-initialization with unit selector support
  function initializeStoreDistance() {
    const distanceElement = document.getElementById('distance-text');
    const storeData = window.storeLocationData;
    
    if (!distanceElement || !storeData) {
      return;
    }

    let calculator = null;

    // Initialize calculator
    function createCalculator() {
      if (window.StoreDistanceCalculator) {
        calculator = new StoreDistanceCalculator({
          storeLatitude: storeData.latitude,
          storeLongitude: storeData.longitude,
          distanceElement: distanceElement,
          unit: storeData.unit || 'km'
        });
        return true;
      }
      return false;
    }

    const setupUnitSelector = () => {
      const unitSelector = document.getElementById('unit-selector');
      if (unitSelector) {
        unitSelector.addEventListener('change', (event) => {
          const newUnit = event.target.value;
          if (calculator) {
            calculator.setUnit(newUnit);
          }
        });
      }
    };


    // Try to initialize immediately
    if (createCalculator()) {
      setupUnitSelector();
    } else {
      // Wait for script to be available with timeout
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds with 100ms intervals
      
      const checkForScript = setInterval(() => {
        attempts++;
        if (createCalculator()) {
          clearInterval(checkForScript);
          setupUnitSelector();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkForScript);
          distanceElement.textContent = 'Distance calculator unavailable';
        }
      }, 100);
    }
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStoreDistance);
  } else {
    initializeStoreDistance();
  }

})();
